import { ApiPostCrystalOrder, ApiPostCrystalOrderPay } from "@/apis/crystal";
import { userInitialize } from "@/apis/user";
import { GenderEnum, MemberType } from "@/enums/mateEnums";
import i18n from "@/locales";
import api from "@/modules/api";
import { inApp, isAndroidWebview, isIOSWebview } from "@/modules/common";
import { postMessageToSW } from "@/modules/message/clientMsg";
import { SWMessageEvent } from "@/modules/message/enums/message";
import { LoginMessage } from "@/modules/message/types/message";
import { useMate } from "@/stores/mate";
import { usePushChannel } from "@/stores/push-channel";
import { useUI } from "@/stores/ui";
import { StorageRemove } from "@/tools/localStorage";
import { sessionStorageGet } from "@/tools/sessionStorage";
import { User } from "@/types/entity";
import { useDefaultAvatar } from "@/use/common";
import { routerHome } from "@/use/go";
import moment from "moment-timezone";
import { defineStore } from "pinia";
import { showConfirmDialog, showLoadingToast } from "vant";
import { useGlobalModal } from "./globalModal";
import { useGlobalWindow } from "./globalWindow";
import { useJsBridgeStore } from "./jsBridge";
import { useWorldview } from "./worldview";
import { nextTick } from "vue";
import { Reviewer } from "@/types/reviewer";
import { ReceivedType, Subscription } from "@/types/payment";
import { getLanguageModelRatio, LanguageModel } from "@/enums/chatEnums";
import { fixNumber } from "@/components/Mission/MissionStat";

export const useGlobalUser = defineStore("globalUser", {
  state: () => {
    return {
      user: {
        isNsfwBlurry: 1,
        unpaidNsfwMessageMaxDaily: 6,
      } as User,
      id: 0,
      name: "Guest",
      avatarUrl: useDefaultAvatar(),
      settingMateLanguageId: 0,
      settingDisplayLanguageId: import.meta.env.VITE_IS_CN ? 2 : 1,
      settingMateChatColor: 1,
      currentMateId: 0,
      email: "",
      mateCount: 0,
      token: 0,
      tokenSpend: 0,
      bio: "",
      oaiModel: null as null | string,
      isInit: false,
      coinAmount: 0,
      coinAmountToday: 0,
      coinVoteToday: 0,
      fetching: false,
      goldenTicketAmount: 0,
      reviewer: [] as Reviewer[],
      creator_score: 0,
      country: "",

      loginModal: {
        show: false,
        type: "login",
        reason: "",
      },
      nsfwAgeConfirm: {
        show: false,
        callback: null as null | (() => void | Promise<void>),
      },

      subscriptions: [] as Subscription[],
      scopedCurrentMateId: 0 as number,
      invitationCode: "",
      impersonating: false,
      registerCallback: undefined as undefined | (() => void | Promise<void>),
    };
  },
  getters: {
    rewardCount(state): number {
      if (!state.user.id) return 0;
      let count = 0;
      if (
        !state.user.dtLastClaimChest ||
        moment(state.user.dtLastClaimChest).add(1, "day").isBefore(moment())
      ) {
        count++;
      }

      if (
        state.user.memberTypes?.includes(MemberType.Premium) &&
        (!state.user.dtLastClaimPremiumMemberChest ||
          moment(state.user.dtLastClaimPremiumMemberChest)
            .add(1, "day")
            .isBefore(moment()))
      ) {
        count++;
      }

      if (
        state.user.memberTypes?.includes(MemberType.Standard) &&
        (!state.user.dtLastClaimStandardMemberChest ||
          moment(state.user.dtLastClaimStandardMemberChest)
            .add(1, "day")
            .isBefore(moment()))
      ) {
        count++;
      }

      if (state.user.claimedChestCount >= 5) {
        count++;
      }

      return count;
    },
    isLogin: (state): boolean => {
      return state.id !== 0 && (!!state.email || !!state.user.phone);
    },
    isAnonymousUser: (state) => {
      return state.id !== 0 && !state.email && !state.user.phone;
    },
    isHideNSFW: (state) => {
      return (
        state.user.country === "CN" ||
        i18n.global.locale.value === "zh-CN" ||
        !state.user?.isNsfwVisible
      );
    },
    isCN: (state) => {
      return (
        state.user.country === "CN" || i18n.global.locale.value === "zh-CN"
      );
    },
    isCNUser(): boolean {
      return this.isHideNSFW;
    },
    isReviewer: (state) => {
      return state.reviewer?.length > 0;
    },
    hasRedeemedGift: (state) => {
      return state.user.unpaidNsfwMessageMaxDaily > 30;
    },
    isBasicMember: (state) => {
      return state.user.memberTypes?.includes(MemberType.Basic) ?? false;
    },
    isStandardMember: (state) => {
      return state.user.memberTypes?.includes(MemberType.Standard) ?? false;
    },
    isPremiumMember: (state) => {
      return state.user.memberTypes?.includes(MemberType.Premium) ?? false;
    },
    getMemberType: (state) => {
      if (state.user.memberTypes?.includes(MemberType.Premium)) {
        return MemberType.Premium;
      }
      if (state.user.memberTypes?.includes(MemberType.Standard)) {
        return MemberType.Standard;
      }
      if (state.user.memberTypes?.includes(MemberType.Basic)) {
        return MemberType.Basic;
      }
      return MemberType.Free;
    },
  },
  actions: {
    async init() {
      const user: User = sessionStorageGet("user");
      if (!user || !user.id) {
        const res = await api.readUserHome();

        if (res.data.isImpersonating) {
          // 模拟登录
          this.impersonating = true;
        }

        await this.login(res.data);
      } else {
        await this.login(user);
      }
      this.isInit = true;
    },
    async refresh() {
      const res = await api.readUserHome();
      this.updateUser(res.data);
    },
    async login(user: User) {
      this.updateUser(user);
      await this.afterLogin();
      // this.loginModal.show = false;
    },
    updateUser(user: User) {
      if (!user || !user.id) return;
      this.user = user;
      this.user.isNsfwBlurry = user.isNsfwBlurry;
      this.user.isNsfwVisible = user.isNsfwVisible;
      this.name = user.name;
      this.avatarUrl = user.mediaUrl ? user.mediaUrl : useDefaultAvatar();
      this.id = user.id!;
      this.settingMateLanguageId = user.settingMateLanguageId;
      this.settingDisplayLanguageId = user.settingDisplayLanguageId;
      this.settingMateChatColor = user.settingMateChatColor;
      this.mateCount = user.mateCount;
      this.token = user.token;
      this.tokenSpend = user.tokenSpend;
      this.bio = user.bio;
      this.oaiModel = user.oaiModel;
      this.currentMateId = user.mateId;
      this.coinAmount = user.coinAmount;
      this.coinAmountToday = user.coinAmountToday;
      this.coinVoteToday = user.coinVoteToday;
      this.goldenTicketAmount = user.goldenTicketAmount;
      this.reviewer = user.reviewer;
      this.email = user.email;
      this.creator_score = user.creator_score;
      this.country = user.country;
    },
    async updateUserApi(param: Partial<User> = {}) {
      await api.updateUser(param);
      this.refresh();
    },
    async updateUserExtraConfig(
      param: Partial<{
        viewMode: "avatar" | "portrait";
        lineMateCount: number;
        taskScreen: boolean;
        taskScreenX: number;
        taskScreenY: number;
      }> = {}
    ) {
      let extraConfig = this.user.extraConfig || {};
      extraConfig = { ...extraConfig, ...param };
      await api.updateUser({ extraConfig });
      this.refresh();
    },
    logout(reload = true) {
      this.id = 0;
      this.name = "Guest";
      this.avatarUrl = useDefaultAvatar();
      this.currentMateId = 0;
      const jsBridgeStore = useJsBridgeStore();
      jsBridgeStore.callLogoutHandler();
      localStorage.clear();
      sessionStorage.clear();
      postMessageToSW({ event: SWMessageEvent.logout });
      if (reload) {
        setTimeout(() => {
          window.$router.replace("/").then(() => {
            location.reload();
          });
        }, 200);
      }
    },
    async updateCurrentMateId(force = false) {
      await Promise.all([
        this.updateUserApi({ mateId: this.currentMateId }),
        // this.refreshSubscription(force),
      ]);
    },
    // async refreshSubscription(force = false) {
    //   if (!force && this.subscriptions.length > 0) return;
    //   else this.subscriptions = [];
    //   const subscription = await api.getAllSubscription();
    //   this.subscriptions = subscription.itemzz.sort(
    //     (a: Subscription, b: Subscription) =>
    //       (b.subscriptionType - a.subscriptionType) * 10 +
    //       (b.billingFrequency! - a.billingFrequency!)
    //   );
    // },

    async refreshUser() {
      this.fetching = true;
      try {
        await this.refresh();
      } finally {
        this.fetching = false;
      }
    },
    /**
     * 获取基础数据
     */
    async afterLogin() {
      if (!this.id) return;
      const mate = useMate();
      const ui = useUI();
      // 1. 全局依赖mate列表，先请求
      ui.showMateLoading = true;
      const worldviewStore = useWorldview();
      worldviewStore.getWorldviewList();
      mate
        .getMateList(true)
        .then(() => {
          ui.showMateLoading = false;
          setTimeout(() => {
            useGlobalWindow().showLoading = false;

            const diff = moment().diff(moment(this.user.dtCreate), "minute");

            // 如果用户创建时间小于1分钟，并且mate列表为空，则创建一个sample OC
            if (diff <= 1 && mate.list.length === 0) {
              nextTick(() => {
                if (window.$router.currentRoute.value.name !== "mateCreate")
                  window.$router.push({
                    name: "mateCreate",
                  });
                if (this.registerCallback) {
                  this.registerCallback();
                  this.registerCallback = undefined;
                }
              });
            }
          }, 100);
        })
        .catch((e) => {
          console.log(e);
        });
      // popular.getMatePopularList();
      // popular.getOrFetchMateTags();
      this.updateCurrentMateId();
      // ui.showMateLoading = false;
      // 2. 请求其他数据
      // 2.1 非阻塞数据
      // FIXME 游戏列表改为在component中请求
      // useGalGameHome().initHome();
      // await Promise.all([]);
      // 3. 注册websocket
      try {
        if (!inApp) {
          Notification.requestPermission();
        }
      } catch (e) {
        // ignore
      }
      const pusher = usePushChannel();
      pusher.startPusherListen();
      console.log("client ws connected");

      postMessageToSW({
        event: SWMessageEvent.login,
        data: { userId: this.id, langId: this.settingDisplayLanguageId },
      } as LoginMessage);
    },
    async anonymousUserInit(params: { gender: GenderEnum }) {
      if (this.isLogin) {
        return;
      }
      const res = await userInitialize(params);
      this.updateUser(res.item);
      const mate = useMate();
      await mate.getMateList();
    },
    async deleteUser() {
      await api.deleteUserHome();
      this.logout(true);
      await routerHome();
    },

    async clickPayItem(
      {
        id,
        quantity,
        buildNumber,
      }: { id: number; quantity: number; buildNumber: number },
      idx?: number
    ) {
      if (
        (isAndroidWebview && buildNumber >= 21) ||
        (isIOSWebview && buildNumber >= 22)
      ) {
        console.log("buildNumber: ", buildNumber);

        const loading = showLoadingToast({
          message: i18n.global.t("Message.creatingOrder"),
          duration: 0,
        });
        const { item } = await ApiPostCrystalOrder(id);
        loading.close();
        window.$router.push({
          query: {
            packageId: id,
            method: "wechat",
            uuid: item.uuid,
          },
        });
        return;
      }
      if (idx !== undefined) {
        const paymentUrls = [
          `https://www.xiaohongshu.com/goods-detail/67f78cda31f0e70001a00db1`,
          `https://www.xiaohongshu.com/goods-detail/67f78cda31f0e70001a00db4`,
          `https://www.xiaohongshu.com/goods-detail/67f78cda31f0e70001a00db7`,
          `https://www.xiaohongshu.com/goods-detail/67f78cda31f0e70001a00dba`,
        ];

        window.open(paymentUrls[idx], "_blank");
        return;
      }
      if (useGlobalUser().isCNUser)
        return showConfirmDialog({
          message: i18n.global.t("purchase.chineseBan"),
          showCancelButton: false,
          confirmButtonText: i18n.global.t("Buttons.confirm"),
        });
      if (!this.isLogin) {
        this.loginModal.show = true;
        return;
      }
      const loading = showLoadingToast({
        duration: 0,
        message: i18n.global.t("Message.paymentRedirecting"),
      });
      const { item } = await ApiPostCrystalOrder(id);
      const { data } = await ApiPostCrystalOrderPay(
        item.uuid,
        "crystal",
        quantity
      );
      location.href = data.paymentUrl;
      setTimeout(loading.close, 2000);
    },
    updateUserPartial(param: Partial<User>) {
      this.user = { ...this.user, ...param };
    },
    async confirmAge(checked: boolean) {
      const params: Partial<User> = {};
      if (checked) {
        params.isAdult = true;
        params.isNsfwVisible = 1;
      } else {
        params.isNsfwVisible = 0;
      }
      await this.updateUserApi(params);
    },

    async checkPaymentSuccess(
      list: Array<{ type: ReceivedType; amount: number }>,
      type: string,
      subscriptionType: string
    ) {
      const loading = showLoadingToast(
        i18n.global.t(`Payment.paymentChecking`)
      );
      if (this.user.subscriptionType === Number(subscriptionType)) {
        loading.close();
        useGlobalModal().openReceivedModal(list, type);
        StorageRemove("paymentReceivedList");
      } else {
        await new Promise((r) => setTimeout(r, 1000));
        await this.updateCurrentMateId(true);
        loading.close();
        this.checkPaymentSuccess(list, type, subscriptionType);
      }
    },

    openLoginModal(type: "register" | "login" = "login") {
      this.loginModal.show = true;
      this.loginModal.type = type;
    },

    isFreeModel() {
      return (
        (this.isBasicMember &&
          this.user.preferredModel === LanguageModel.QwenFlash) ||
        (this.isStandardMember &&
          [LanguageModel.QwenFlash, LanguageModel.Qwen30bA3b].includes(
            this.user.preferredModel
          )) ||
        (this.isPremiumMember &&
          [
            LanguageModel.QwenFlash,
            LanguageModel.Qwen30bA3b,
            LanguageModel.QwenMax,
          ].includes(this.user.preferredModel))
      );
    },
    getPowerCostByModel(powerAmount: number) {
      if (this.isFreeModel()) return 0;
      return fixNumber(
        powerAmount * getLanguageModelRatio(this.user.preferredModel)
      );
    },
  },
});
